import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Button,
  Space,
  Table,
  Select,
  notification,
} from "antd";
import queryString from "query-string";
import { assignSpecialDeliveryTour } from "../redux/actions/specialDeliveryActions";

const { Title } = Typography;
const { Option } = Select;

const vehicleOptions = [
  { id: "VEH-1", label: "Vehicle A" },
  { id: "VEH-2", label: "Vehicle B" },
];

const driverOptions = [
  { id: "DRV-1", label: "Driver A" },
  { id: "DRV-2", label: "Driver B" },
];

const helperOptions = [
  { id: "HLP-1", label: "Helper A" },
  { id: "HLP-2", label: "Helper B" },
];

const columns = [
  { title: "Order ID", dataIndex: "orderId", key: "orderId" },
  { title: "Sub-category", dataIndex: "subCategory", key: "subCategory" },
  { title: "Reason Code", dataIndex: "reasonCode", key: "reasonCode" },
];

const SpecialPlanningBoard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { authToken, clientId } = queryString.parse(location.search);

  const specialOrders = useSelector(
    (state) => state?.specialDelivery?.specialOrders || []
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [vehicleId, setVehicleId] = useState(undefined);
  const [driverId, setDriverId] = useState(undefined);
  const [helperId, setHelperId] = useState(undefined);
  const [isAssigning, setIsAssigning] = useState(false);

  const handleError = useCallback(() => {
    navigate(`/error-fallback${location.search}`);
  }, [navigate, location.search]);

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const canConfirm =
    selectedRowKeys.length > 0 && Boolean(vehicleId) && Boolean(driverId);

  const onConfirm = () => {
    const requestObject = {
      orderIds: selectedRowKeys,
      vehicleId,
      driverId,
      helperId,
    };
    const authParams = { clientId, authToken };

    setIsAssigning(true);

    dispatch(
      assignSpecialDeliveryTour(requestObject, authParams, navigate, handleError)
    )
      .then((res) => {
        if (res) {
          notification.success({
            message: "Special Delivery Tour Assigned",
            placement: "bottomRight",
          });
          setSelectedRowKeys([]);
          return;
        }
        notification.error({
          message: "Error Assigning Special Delivery Tour",
          placement: "bottomRight",
        });
      })
      .catch(() => {
        notification.error({
          message: "Error Assigning Special Delivery Tour",
          placement: "bottomRight",
        });
      })
      .finally(() => {
        setIsAssigning(false);
      });
  };

  return (
    <div style={{ padding: "24px" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Title level={5}>Special Delivery Planning</Title>

        <Row gutter={16}>
          <Col>
            <div data-testid="vehicle-select">
              <Select
                virtual={false}
                aria-label="Select vehicle"
                style={{ width: 180 }}
                placeholder="Select vehicle"
                value={vehicleId}
                onChange={(value) => setVehicleId(value)}
              >
                {vehicleOptions.map((vehicle) => (
                  <Option key={vehicle.id} value={vehicle.id}>
                    {vehicle.label}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col>
            <div data-testid="driver-select">
              <Select
                virtual={false}
                aria-label="Select driver"
                style={{ width: 180 }}
                placeholder="Select driver"
                value={driverId}
                onChange={(value) => setDriverId(value)}
              >
                {driverOptions.map((driver) => (
                  <Option key={driver.id} value={driver.id}>
                    {driver.label}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col>
            <div data-testid="helper-select">
              <Select
                virtual={false}
                allowClear
                aria-label="Select helper"
                style={{ width: 180 }}
                placeholder="Select helper"
                value={helperId}
                onChange={(value) => setHelperId(value)}
              >
                {helperOptions.map((helper) => (
                  <Option key={helper.id} value={helper.id}>
                    {helper.label}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col>
            <Button
              type="primary"
              disabled={!canConfirm || isAssigning}
              loading={isAssigning}
              onClick={onConfirm}
            >
              Confirm
            </Button>
          </Col>
        </Row>

        <Table
          bordered
          rowKey={(record) => record.orderId ?? record.id}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={specialOrders}
          pagination={false}
        />
      </Space>
    </div>
  );
};

export default SpecialPlanningBoard;
